import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import Layout from "components/layout/Layout";
import AccountListForAdmin from "pages/AccountListForAdmin";
import AccountListForClient from "pages/AccountListForClient";
import ClientList from "pages/ClientList";
import ClientProfile from "pages/ClientProfile";
import ClientProfileForAdmin from "pages/ClientProfileForAdmin";
import CompaniesForAdmin from "pages/CompaniesForAdmin";
import CompaniesForClient from "pages/CompaniesForClient";
import CompanyPageForAdmin from "pages/CompanyPageForAdmin";
import CompanyPageForClient from "pages/CompanyPageForClient";
import EditCompanyForAdmin from "pages/EditCompanyForAdmin";
import EditCompanyForClient from "pages/EditCompanyForClient";
import NotFound from "pages/NotFound";
import RequestsForAdmin from "pages/RequestsForAdmin";
import RequestsForClient from "pages/RequestsForClient";
import SignIn from "pages/SignIn";
import SignUp from "pages/SignUp";
import TopUpsForClient from "pages/TopUpsForClient";
import RequireAuth from "utils/RequireAuth";

import AdAccountWiseDashboard from "pages/AdAccountWiseDashboard";
import ChangePassword from "pages/ChangePassword";
import ClientWiseDashboard from "pages/ClientWiseDashboard";
import Dashboard from "pages/Dashboard";
import ForgotPassword from "pages/ForgotPassword";
import Health from "pages/Health";
import LandingPage from "pages/LandingPage";
import LogsForAdmin from "pages/LogsForAdmin";
import LogsForClient from "pages/LogsForClient";
import PrivacyPolicy from "pages/PrivacyPolicy";
import ResetPassword from "pages/ResetPassword";
import TermsConditions from "pages/TermsConditions";
import TopUpsForAdmin from "pages/TopUpsForAdmin";
import VerifyCompanyForAdmin from "pages/VerifyCompanyForAdmin";
import VerifyEmail from "pages/VerifyEmail";
import { setUser } from "store/auth/authSlice";

import AboutForAdspresso from "pages/AboutForAdspresso";
import AboutForRentAccount from "pages/AboutForRentAccount";
import AdRules from "pages/AdRules";
import AdspressoCampaignPageForClient from "pages/AdspressoCampaignPageForClient";
import AdspressoForClient from "pages/AdspressoForClient";
import Analytics from "pages/Analytics";
import CampaignsForClient from "pages/CampaignsForClient";
import DriveForClient from "pages/DriveForClient";
import { PublishTracking } from "pages/PublishTracking";
import "react-phone-input-2/lib/style.css";
import "./styles/customStyles.css";

import TrialEndingModal from "components/TrialEndingModal";
import ActivityDashboard from "pages/ActivityDashboard";
import AiHub from "pages/AiHub";
import EditBankForClient from "pages/EditBankForClient";
import PricingPlans from "pages/Pricing";
import PricingPageForClient from "pages/PricingPageForClient";
import SignalGateway from "pages/SignalGateway";
import Subscription from "pages/Subscription";
import { selectSubscription } from "store/Subscription/SubscriptionSlice";
import PlanUsage from "pages/PlanUsage";
import BuyCredits from "components/adspresso/BuyCredits";
import CallCenter from "pages/CallCenter";
import Lander from "pages/Lander";
import WhatsAppBot from "pages/WhatsAppBot";
import PageanalyticsForClient from "pages/PageanalyticsForClient";

function App() {
  const dispatch = useDispatch();
  const location = useLocation();
  const userInfo = useSelector((state) => state.auth?.userInfo);
  const subscriptionInfo = useSelector(selectSubscription);
  const [showTrialModal, setShowTrialModal] = useState(false);

  useEffect(() => {
    const localUserInfo = JSON.parse(localStorage.getItem("forexUser"));
    if (localUserInfo) dispatch(setUser(localUserInfo));
  }, []);

  useEffect(() => {
    if (subscriptionInfo?.status === "trialing") {
      // Don’t show trial modal on subscription or not-found pages
      const hideOnRoutes = ["/subscription", "/not-found"];

      if (hideOnRoutes.some(route => location.pathname.startsWith(route))) {
        return;
      }

      const storedDate = localStorage.getItem("hideTrialModalDate");
      const today = new Date().toISOString().split("T")[0];

      if (storedDate && storedDate !== today) {
        localStorage.removeItem("hideTrialModal");
        localStorage.removeItem("hideTrialModalDate");
      }

      if (!localStorage.getItem("hideTrialModal")) {
        setShowTrialModal(true);
      }
    }
  }, [subscriptionInfo]);

  return (
    <>
      <Routes>
        <Route path="/health" element={<Health />} />
        <Route path="/" element={<Layout />}>
          {/* PUBLIC */}

          <Route path="signIn" element={<SignIn />} />
          <Route path="signUp" element={<SignUp />} />
          <Route path="/forgot-password-change" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/change-password" element={<ChangePassword />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/terms-conditions" element={<TermsConditions />} />
          <Route path="/home" element={<LandingPage />} />

          {/* PROTECTED */}

          {!userInfo && <Route path="/" element={<Navigate to="/signIn" replace />} />}

          {userInfo &&
            (userInfo?.role == "admin" ||
              userInfo?.role == "media" ||
              userInfo?.role == "finance") && (
              <Route element={<RequireAuth allowedRoles={["admin"]} />}>
                <Route path="/" element={<Navigate to="/clients" replace />} />
                <Route path="/clients" element={<ClientList />} />
                <Route path="/clients/:id" element={<ClientProfileForAdmin />} />
                <Route path="/requests" element={<RequestsForAdmin />} />
                <Route path="/accounts" element={<AccountListForAdmin />} />
                <Route path="/companies" element={<CompaniesForAdmin />} />
                <Route path="/companies/:id" element={<CompanyPageForAdmin />} />
                <Route path="/companies/:id/verify" element={<VerifyCompanyForAdmin />} />
                <Route path="/companies/:id/edit" element={<EditCompanyForAdmin />} />
                <Route path="/topup" element={<TopUpsForAdmin />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/dashboard/clients/:id" element={<ClientWiseDashboard />} />
                <Route path="/dashboard/ads-accounts/:id" element={<AdAccountWiseDashboard />} />
                <Route path="/adspresso-analytics" element={<Analytics />} />
                <Route path="/adspresso-analytics/clients/:id" element={<ClientWiseDashboard />} />
                <Route path="/logs" element={<LogsForAdmin />} />
                <Route path="/activity-analytics" element={<ActivityDashboard />} />
              </Route>
            )}

          {userInfo && userInfo?.role == "client" && (
            <Route element={<RequireAuth allowedRoles={["client"]} />}>
              <Route path='/' element={<Navigate to={'/launchpad'} replace />} />
              <Route path='/profile' element={<ClientProfile />} />
              <Route path='/accounts' element={<AccountListForClient />} />
              <Route path='/requests' element={<RequestsForClient />} />
              <Route path='/companies' element={<CompaniesForClient />} />
              <Route path='/companies/:id' element={<CompanyPageForClient />} />
              <Route path='/companies/:id/edit' element={<EditCompanyForClient />} />
              <Route path='/companies/:id/edit/bank/' element={<EditBankForClient />} />
              <Route path='/topup' element={<TopUpsForClient />} />
              <Route path='/about-adsmanager' element={<AboutForAdspresso />} />
              <Route path='/about-rent' element={<AboutForRentAccount />} />
              <Route path='/uploads' element={<DriveForClient />} />
              <Route path='/launchpad' element={<AdspressoForClient />} />
              <Route path='/launchpad/:id' element={<AdspressoCampaignPageForClient />} />
              <Route path='/campaigns' element={<CampaignsForClient />} />
              <Route path='/generate' element={<AiHub />} />
              <Route path='/signal-gateway' element={<SignalGateway />} />
              <Route path='/pricing' element={<PricingPlans />} />
              <Route path='/pricing/:action' element={<PricingPageForClient />} />
              <Route path='/pricing/:action/:customerId' element={<PricingPageForClient />} />
              <Route path="/subscription" element={<Subscription />} />
              <Route path="/plan-usage" element={<PlanUsage />} />
              <Route path="/buy-credits" element={<BuyCredits />} />
              <Route path="/call-center" element={<CallCenter />} />
              <Route path="/lander" element={<Lander />} />
              <Route path="/whatsapp-bot" element={<WhatsAppBot />} />
              <Route path="/publish-tracking/:id" element={<PublishTracking />} />
              <Route path="/rules/" element={<AdRules />} />
              <Route path="/logs" element={<LogsForClient />} />
              <Route path="/page-analytics" element={<PageanalyticsForClient />} />
            </Route>
          )}

          {/* CATCH ALL */}

          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>

      {userInfo?.role === "client" && 
        <TrialEndingModal isOpen={showTrialModal} onClose={() => setShowTrialModal(false)} />
      }
      

      {/* OFFLINE */}

      {/* <Offline>
        <Routes>
          <Route
            path='*'
            element={
              <OfflinePage />
            }
          />
        </Routes>
      </Offline> */}
    </>
  );
}

export default App;
